import { usePurchaseContext } from '~/purchase/context';
import { Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, FormContainer, Modal, notify, Flex } from '@wyz/ui';
import { useForm, useWatch } from 'react-hook-form';
import { useManageQuotePricingPolicies } from '~/purchase/api';
import { duplicatePolicyFormSchema } from '~/purchase/schemas';
import { IDuplicatePolicyForm } from '~/purchase/types';
import { ConflictedPoliciesTable } from '~/purchase/components';
import {
  useMyCustomers,
  usePricingPolicyErrorTranslation,
} from '~/common/hooks';
import { invariant, SUPPLIERS_TYPES_IDS } from '@wyz/utils';
import { CUSTOMER_LEVEL } from '@wyz/types';
import { omit } from 'lodash-es';

type QuoteDuplicatePolicyModalProps = {
  onClose: () => void;
  policyGroupId: number;
  customerId: string;
};

function QuoteDuplicatePolicyModal({
  customerId,
  onClose,
  policyGroupId,
}: QuoteDuplicatePolicyModalProps) {
  const methods = useForm<IDuplicatePolicyForm>({
    resolver: zodResolver(duplicatePolicyFormSchema),
    defaultValues: {
      customerIds: [],
      action: 'SAVE_AS_DRAFT',
    },
  });

  const watchedCustomers = useWatch({
    control: methods.control,
    name: 'customerIds',
    defaultValue: [],
  });

  const manageQuotePricingPolicies = useManageQuotePricingPolicies({
    getConflicted: {
      enabled: true,
      args: {
        id: policyGroupId,
        customerIds: watchedCustomers.map((c) => c.value),
      },
      staleTime: 0,
      gcTime: 0,
    },
    duplicate: {
      enabled: true,
    },
  });
  const getCustomersByUser = useMyCustomers();
  const { state } = usePurchaseContext();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();
  invariant(state.targetCustomer);
  const getCustomerSupplierTypes = (id: string) => {
    const customer = getCustomersByUser.data?.data.find(
      (customer) => customer.id === id,
    );
    invariant(customer);

    if (customer.level > CUSTOMER_LEVEL.N1) {
      return [];
    }

    return [
      SUPPLIERS_TYPES_IDS.MANUFACTURER,
      SUPPLIERS_TYPES_IDS.WYZ_DISTRIBUTOR,
    ];
  };

  return (
    <Modal.Container open={true} onClose={onClose} size='xl' backdrop='static'>
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.quote.pricing_policy.duplicate_modal.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormContainer
          methods={methods}
          submitHandler={(form) => {
            manageQuotePricingPolicies.duplicate.mutate(
              {
                id: policyGroupId.toString(),
                body: {
                  ...omit(form, 'customerIds'),
                  customers: form.customerIds.map((customer) => ({
                    id: customer.value,
                    supplierTypes: getCustomerSupplierTypes(customer.value),
                  })),
                },
              },
              {
                onSuccess: () => {
                  notify.success(
                    <Trans i18nKey='app.common.messages.success' />,
                    { id: 'toast-success-quote-pricing-policy-duplicated' },
                  );
                  onClose();
                },
                onError: (error) => {
                  notify.error(
                    <Trans i18nKey={resolveErrorKey(error.message)} />,
                    {
                      id: 'toast-error-quote-pricing-policy-duplicate-failed',
                    },
                  );
                },
              },
            );
          }}
        >
          <div className='space-y-4'>
            <ConflictedPoliciesTable
              conflictedPolicies={
                manageQuotePricingPolicies.getConflicted.data?.data ?? []
              }
              customerId={customerId}
              isLoadingPolicies={
                manageQuotePricingPolicies.getConflicted.isLoading
              }
              customer={state.targetCustomer}
            />

            <Card>
              <Card.Body>
                <Flex className='space-x-2' justifyContent='end'>
                  <Button
                    id='cancel'
                    variant='secondary'
                    type='button'
                    onClick={onClose}
                    disabled={
                      manageQuotePricingPolicies.duplicate.isLoading ||
                      manageQuotePricingPolicies.getConflicted.isLoading
                    }
                  >
                    <Trans i18nKey='app.common.button.cancel' />
                  </Button>
                  <Button
                    id='apply'
                    variant='primary'
                    type='submit'
                    isLoading={
                      manageQuotePricingPolicies.duplicate.isLoading ||
                      manageQuotePricingPolicies.getConflicted.isLoading
                    }
                  >
                    <Trans i18nKey='app.common.button.apply' />
                  </Button>
                </Flex>
              </Card.Body>
            </Card>
          </div>
        </FormContainer>
      </Modal.Body>
    </Modal.Container>
  );
}

export default QuoteDuplicatePolicyModal;
